import { useEffect, useState, useRef } from "react";
import api, { API } from "../../lib/api";
import { SocietyPageHeader, Loading, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { FolderOpen, Upload, FileText, Download, Trash2, Eye } from "lucide-react";

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [view, setView] = useState(null);
  const [meta, setMeta] = useState({ category: "General", title: "" });
  const fileRef = useRef();
  const load = () => api.get("/tools/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const doUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("title", meta.title || file.name); fd.append("category", meta.category);
    try { await api.post("/tools/documents/upload", fd); toast.success("Document uploaded"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const del = async (id) => { if (!window.confirm("Delete document?")) return; await api.delete(`/tools/documents/${id}`); toast.success("Deleted"); load(); };
  const download = (id) => window.open(`${API}/tools/documents/${id}/download`, "_blank");

  if (!docs) return <Loading />;
  return (
    <div>
      <SocietyPageHeader icon={FolderOpen} tone="orange" title="Document Management" subtitle="Securely store bye-laws, minutes, contracts and important records.">
        <input ref={fileRef} type="file" hidden onChange={doUpload} data-testid="doc-upload-input" />
        <Input placeholder="Category" value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} className="w-36 h-10" />
        <Button onClick={() => fileRef.current.click()} disabled={uploading} className="rounded-full gap-2" data-testid="doc-upload"><Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload"}</Button>
      </SocietyPageHeader>
      {docs.length === 0 ? <EmptyState icon={FolderOpen} title="No documents yet" subtitle="Upload files or save generated documents from Free Tools. Private to your society only." /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => (
            <div key={d.doc_id} className="bg-card border rounded-xl p-4" data-testid={`doc-card-${d.doc_id}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><FileText className="w-5 h-5" /></div>
                <span className="text-xs bg-muted rounded-full px-2 py-0.5">{d.category}</span>
              </div>
              <h3 className="font-medium mt-3 truncate">{d.title}</h3>
              <div className="text-xs text-muted-foreground mt-0.5">{d.kind === "generated" ? "AI Generated" : d.filename} · {(d.created_at || "").slice(0, 10)}</div>
              <div className="flex gap-1 mt-3">
                {d.kind === "generated" ? <Button size="sm" variant="outline" onClick={() => setView(d)} className="gap-1.5"><Eye className="w-3.5 h-3.5" /> View</Button>
                  : <Button size="sm" variant="outline" onClick={() => download(d.doc_id)} className="gap-1.5" data-testid={`doc-download-${d.doc_id}`}><Download className="w-3.5 h-3.5" /> Download</Button>}
                <Button size="sm" variant="ghost" onClick={() => del(d.doc_id)} className="text-destructive ml-auto"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{view?.title}</DialogTitle></DialogHeader>
          <pre className="whitespace-pre-wrap text-sm leading-relaxed">{view?.content}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
